var _a;
import { setupAddEventListener, setupRemoveEventListener, } from "@todesktop/client-util";
import { invoke } from "./invoke.js";
const safeWindowToDesktop = typeof window !== "undefined" && window["todesktop"];
export const createOnAndOffNamespaceListeners = (namespace) => {
    const addEventListener = async (eventName, callback, { ref } = {}) => {
        const event = (await invoke({
            instanceRef: ref,
            namespace,
            eventName,
        }));
        // Todo: receiving an electron event as an arugment no longer happens in desktopify and should be removed as a breaking change in future.
        window["todesktop"]["on"](event, (eventId, ...args) => {
            // Subs are now invoked in desktopify as subscription(eventId, ...args) instead of subscription(eventId, event, ...args);
            // This means that our subscription handlers needs to check if the second argument is an electron event to make sure we ignore it
            if (isEncapsulatedElectronEvent(args[0])) {
                const [, ...correctArgs] = args;
                return callback(...correctArgs);
            }
            else {
                return callback(...args);
            }
        });
        return event;
    };
    const removeEventListener = async (eventName, { ref, eventId }) => {
        await invoke({
            instanceRef: ref,
            namespace,
            eventName,
            eventId,
        });
        window["todesktop"]["off"](eventId);
    };
    const removeAllListeners = async (eventName, { ref } = {}) => {
        return invoke({
            instanceRef: ref,
            namespace,
            method: "removeAllListeners",
            args: [eventName],
        });
    };
    return {
        addEventListener,
        removeEventListener,
        removeAllListeners,
        on: addEventListener,
        off: removeEventListener,
    };
};
function legacyWarning(eventName) {
    // eslint-disable-next-line no-console
    console.warn(`[todesktop-client] ${eventName} is a legacy event and may be removed in future.
        Contact support or look through our typescript files to find a suitable replacement.`);
}
function isLegacyArgs(args, legacyMappings) {
    const eventName = args[0];
    const isLegacy = eventName in legacyMappings;
    if (isLegacy)
        legacyWarning(eventName);
    return isLegacy;
}
const addEventListenerArgsAreLegacy = (args, legacyMappings) => {
    return isLegacyArgs(args, legacyMappings);
};
const removeEventListenerArgsAreLegacy = (args, legacyMappings) => {
    return isLegacyArgs(args, legacyMappings);
};
export const createOnAndOffNamespaceListenersWithCompatiblityForLegacyEvents = (legacyMappings, namespace) => {
    const addEventListener = (...args) => addEventListenerArgsAreLegacy(args, legacyMappings)
        ? setupAddEventListener(legacyMappings)(...args)
        : createOnAndOffNamespaceListeners(namespace).addEventListener(...args);
    const removeEventListener = (...args) => removeEventListenerArgsAreLegacy(args, legacyMappings)
        ? setupRemoveEventListener(legacyMappings)(...args)
        : createOnAndOffNamespaceListeners(namespace).removeEventListener(...args);
    /**
     * Return types of `setupAddEventListener` and `setupRemoveEventListener` are inconsistent with the
     * `NamespaceListenersReturnApi` api. Coercing to `T=Events` solves for this, but also means that
     * `LegacyEvents` won't show up in intellisense. I imagine that this is probably for the best, since
     * legacy events will be deprecated and we should be nudging users away from using them.
     */
    const coercedType = {
        addEventListener,
        removeEventListener,
        removeAllListeners: createOnAndOffNamespaceListeners(namespace).removeAllListeners,
        on: addEventListener,
        off: removeEventListener,
    };
    return coercedType;
};
export const isDev = ((_a = safeWindowToDesktop === null || safeWindowToDesktop === void 0 ? void 0 : safeWindowToDesktop.env) === null || _a === void 0 ? void 0 : _a.NODE_ENV) === "dev";
const isEncapsulatedElectronEvent = (val) => {
    return isObject(val) && "preventDefault" in val && "sender" in val;
};
const isObject = (val) => {
    if (val === null) {
        return false;
    }
    return typeof val === "function" || typeof val === "object";
};
